import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Lock } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { Textarea } from "@/components/atoms/Textarea";
import { Switch } from "@/components/atoms/Switch";
import { ScoringRulesEditor } from "@/modules/leagues/components/ScoringRulesEditor";
import { useCompetitions } from "@/modules/leagues/hooks/useCompetitions";
import { DEFAULT_SCORING_RULES } from "@/modules/leagues/repositories/mock/mockData";
import { useCreateLeague } from "@/modules/leagues/hooks/useCreateLeague";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";
import type { ScoringRule } from "@/modules/leagues/types/league";
import { Skeleton } from "@/components/atoms/Skeleton";
import { ErrorState } from "@/components/organisms/ErrorState";

const STEPS = ["Competição", "Informações", "Pontuação", "Resumo"] as const;

export function CreateLeagueWizard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createLeague = useCreateLeague();
  const [step, setStep] = useState(0);
  const { data: competitions, isLoading: loadingCompetitions, isError: competitionsError, refetch: refetchCompetitions } = useCompetitions();

  const [competitionId, setCompetitionId] = useState("");
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [maxParticipantes, setMaxParticipantes] = useState(20);
  const [privada, setPrivada] = useState(true);
  const [permiteDesafios, setPermiteDesafios] = useState(true);
  const [permitePerguntasBonus, setPermitePerguntasBonus] = useState(true);
  const [permiteImportacaoManual, setPermiteImportacaoManual] = useState(true);
  const [permiteEntradaAposInicio, setPermiteEntradaAposInicio] = useState(true);
  const [rules, setRules] = useState<ScoringRule[]>(DEFAULT_SCORING_RULES);

  const competition = competitions?.find((c) => c.id === competitionId);

  const canAdvance =
    (step === 0 && Boolean(competitionId)) ||
    (step === 1 && nome.trim().length >= 3) ||
    step === 2 ||
    step === 3;

  function handleCreate() {
    if (!competition) return;
    createLeague.mutate(
      {
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        competitionId: competition.id,
        competitionName: competition.name,
        maxParticipantes,
        privada,
        permiteDesafios,
        permitePerguntasBonus,
        permiteImportacaoManual,
        permiteEntradaAposInicio,
        rules,
      },
      {
        onSuccess: (league) => {
          toast({ title: "Liga criada!", description: `Código de convite: ${league.inviteCode}` });
          navigate(ROUTES.leaguePredictions(league.id));
        },
        onError: (err) => toast({ title: "Não foi possível criar a liga", description: err.message, variant: "destructive" }),
      }
    );
  }

  return (
    <div className="space-y-5 pb-8">
      <div className="flex items-center gap-2">
        <button
          onClick={() => (step === 0 ? navigate(ROUTES.home) : setStep((s) => s - 1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="font-display text-base font-bold">Criar Liga</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1.5">
        {STEPS.map((label, idx) => (
          <div key={label} className="flex flex-1 flex-col items-center gap-1.5">
            <div
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold font-display transition-colors",
                idx < step
                  ? "bg-primary text-primary-foreground"
                  : idx === step
                    ? "bg-primary-100 text-primary-700 ring-2 ring-primary dark:bg-primary-900"
                    : "bg-muted text-muted-foreground"
              )}
            >
              {idx < step ? <Check className="h-3.5 w-3.5" /> : idx + 1}
            </div>
            <span className="text-center text-[10px] font-medium text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          {step === 0 && (
            <div className="space-y-2.5">
              <p className="text-sm text-muted-foreground">Escolha a competição que esta liga vai acompanhar.</p>

              {loadingCompetitions && (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              )}

              {competitionsError && (
                <ErrorState
                  title="Não foi possível carregar as competições"
                  description="Verifique sua conexão e tente novamente."
                  onRetry={() => refetchCompetitions()}
                />
              )}

              {competitions?.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCompetitionId(c.id)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors",
                    competitionId === c.id ? "border-primary bg-primary-50 dark:bg-primary-900/20" : "border-border bg-card hover:bg-muted/40"
                  )}
                >
                  <div>
                    <p className="text-sm font-semibold">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.country}</p>
                  </div>
                  {competitionId === c.id && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="nome">Nome da liga</Label>
                <Input id="nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Liga dos Amigos" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="descricao">Descrição (opcional)</Label>
                <Textarea id="descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="max">Máximo de participantes</Label>
                <Input id="max" type="number" min={2} value={maxParticipantes} onChange={(e) => setMaxParticipantes(Number(e.target.value))} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-3">
                <div>
                  <p className="text-sm font-medium">Liga privada</p>
                  <p className="text-xs text-muted-foreground">Só entra com código de convite</p>
                </div>
                <Switch checked={privada} onCheckedChange={setPrivada} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-3">
                <div>
                  <p className="text-sm font-medium">Permitir Desafios (X1)</p>
                </div>
                <Switch checked={permiteDesafios} onCheckedChange={setPermiteDesafios} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-3">
                <div>
                  <p className="text-sm font-medium">Permitir Perguntas Bônus</p>
                </div>
                <Switch checked={permitePerguntasBonus} onCheckedChange={setPermitePerguntasBonus} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-3">
                <div>
                  <p className="text-sm font-medium">Permitir Importação Manual</p>
                </div>
                <Switch checked={permiteImportacaoManual} onCheckedChange={setPermiteImportacaoManual} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-3">
                <div>
                  <p className="text-sm font-medium">Permitir entrada após início</p>
                </div>
                <Switch checked={permiteEntradaAposInicio} onCheckedChange={setPermiteEntradaAposInicio} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Configure a pontuação. <strong>Atenção:</strong> ao criar a liga, essas regras serão congeladas e
                não poderão mais ser alteradas.
              </p>
              <ScoringRulesEditor rules={rules} onChange={setRules} />
            </div>
          )}

          {step === 3 && competition && (
            <div className="space-y-3">
              <div className="rounded-2xl border border-border bg-card p-4">
                <p className="font-display text-base font-bold">{nome}</p>
                <p className="text-xs text-muted-foreground">{competition.name}</p>
                {descricao && <p className="mt-2 text-sm">{descricao}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                  <span>Até {maxParticipantes} participantes</span>
                  <span>·</span>
                  <span>{privada ? "Privada" : "Pública"}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-xl bg-warning/10 px-3 py-2.5 text-xs text-foreground">
                <Lock className="h-4 w-4 shrink-0 text-warning" />
                Ao confirmar, as regras de pontuação abaixo serão congeladas permanentemente.
              </div>

              <ScoringRulesEditor rules={rules} readOnly />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex gap-2 pt-2">
        {step < STEPS.length - 1 ? (
          <Button className="w-full" disabled={!canAdvance} onClick={() => setStep((s) => s + 1)}>
            Continuar <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button className="w-full" isLoading={createLeague.isPending} onClick={handleCreate}>
            Criar Liga <Check className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
